import React from 'react';

/**
 * StoryMedia: one primitive, several deliberately different layouts so
 * solution-page imagery stops feeling like a repeating grid of identical
 * rectangles. Pick a `variant` per story beat.
 *
 * Variants:
 *  - hero    : full-bleed image with gradient overlay; children render on top
 *  - bento   : standalone rounded image tile (varied aspect), image only
 *  - sidebar : compact image beside text (children); good inside a card
 *  - minimal : no image: an editorial text block with an accent rule
 *  - quote   : oversized pull-quote (children = quote text)
 */
export default function StoryMedia({
  variant = 'bento',
  src,
  alt = '',
  className = '',
  imageFit = 'cover',
  children,
}) {
  const heroImgClass =
    imageFit === 'contain'
      ? 'block h-auto w-full max-w-full object-contain object-top'
      : 'h-full w-full object-cover object-top aspect-[16/7]';

  if (variant === 'hero') {
    return (
      <figure
        className={`relative overflow-hidden rounded-2xl border border-ink-200 shadow-md ${className}`}
      >
        {src && (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className={heroImgClass}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/25 to-transparent" />
        {children && (
          <figcaption className="absolute inset-x-0 bottom-0 p-6 sm:p-8">{children}</figcaption>
        )}
      </figure>
    );
  }

  if (variant === 'bento') {
    return (
      <figure
        className={`overflow-hidden rounded-2xl border border-ink-200 bg-surface-muted shadow-xs ring-1 ring-brand-100/40 ${className}`}
      >
        {src && (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="h-full w-full object-cover object-top aspect-[3/2] transition-transform duration-500 hover:scale-[1.03]"
          />
        )}
      </figure>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className={`flex items-center gap-5 ${className}`}>
        {src && (
          <figure className="hidden sm:block flex-shrink-0 overflow-hidden rounded-xl border border-ink-200 shadow-xs">
            <img
              src={src}
              alt={alt}
              loading="lazy"
              className="h-28 w-44 object-cover object-top"
            />
          </figure>
        )}
        <div className="min-w-0">{children}</div>
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div
        className={`border-l-2 border-brand-300 pl-5 py-1 ${className}`}
      >
        {children}
      </div>
    );
  }

  if (variant === 'quote') {
    return (
      <figure className={`relative ${className}`}>
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-6 -left-1 text-7xl leading-none font-serif text-brand-200"
        >
          &ldquo;
        </span>
        <blockquote className="relative pl-8 text-2xl sm:text-3xl font-medium leading-snug tracking-tight text-ink-900">
          {children}
        </blockquote>
      </figure>
    );
  }

  // Fallback: plain rounded image.
  return src ? (
    <figure className={`overflow-hidden rounded-xl border border-ink-200 ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    </figure>
  ) : null;
}
